import { isAfter, isEqual } from 'date-fns';

import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { IKnmRequirementViolation } from 'APIPackage/KnmModule/interfaces/IKnmRequirementViolation';
import { IKnmRequirementsNpa248 } from 'APIPackage/KnmModule/interfaces/IKnmRequirementsNpa248';
import { IKnmString } from 'APIPackage/KnmModule/interfaces/IKnmString';
import { IKnmResultDecision248 } from 'APIPackage/KnmModule/interfaces/IKnmResultDecision248';
import { IKnmViolatedRequirementsMandatory248 } from 'APIPackage/KnmModule/interfaces/IKnmViolatedRequirementsNpa248';
import { IPmResultDecision } from 'APIPackage/KnmModule/interfaces/IPmResultDecision';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IDecisionResultDecision } from 'APIPackage/DecisionModule/interfaces/IDecisionResultDecision';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';

export const getMatchedViolation = (
  prescriptions: (IKnmResultDecision248 | IPmResultDecision | IDecisionResultDecision)[],
  link: string,
  startDate?: Date | undefined,
) => {
  const violations: IKnmRequirementsNpa248[] = [];
  /**
   * Одно и то же обязательное требование может быть нарушено в предписаниях разных контролируемых лиц.
   * Дедуплицируем по НПА + структурная единица + требование:
   * guid самих нарушений у таких записей разный и для сравнения не подходит.
   */
  const addedRequirementKeys = new Set<string>();

  prescriptions.forEach(prescription => {
    // Исключаем предписания без нарушений и на статусе Отменен
    if (prescription.violations && prescription.status !== KnmResultDecisionStatusEnum.CANCELED) {
      prescription.violations.forEach(violation => {
        const newViolation: IKnmRequirementsNpa248 = {
          ...violation,
          structuralUnit: {
            ...violation.structuralUnit,
            mandatoryRequirements: [],
          },
        };

        violation.structuralUnit?.mandatoryRequirementsViolations?.forEach(mr => {
          const requirementKey = `${violation.npaId}_${violation.structuralUnit?.id}_${mr.id}`;

          if (addedRequirementKeys.has(requirementKey)) {
            return;
          }

          const mandatory: IKnmViolatedRequirementsMandatory248 = { ...mr, violations: [] };

          mr.violations?.forEach(viol => {
            if (checkViolationForMatchingRepeatCheck(viol, startDate)) {
              mandatory.violations?.push({
                ...viol,
                includeInSubject: true,
                prescriptionLink: `${link}#result-decisions-violation-${viol.guid}`,
                repeatCorrected: typeof viol.repeatCorrected === 'boolean' ? viol.repeatCorrected : viol.corrected,
                repeatCorrectedInEvent:
                  typeof viol.repeatCorrectedInEvent === 'boolean'
                    ? viol.repeatCorrectedInEvent
                    : viol.correctedInEvent,
              });
            }
          });

          if (mandatory.violations?.length) {
            addedRequirementKeys.add(requirementKey);
            newViolation.structuralUnit.mandatoryRequirements?.push(mandatory);
          }
        });

        if (newViolation.structuralUnit.mandatoryRequirements?.length) {
          violations.push(newViolation);
        }
      });
    }
  });

  return violations;
};

/**
 * Плановая дата устранения нарушения. Дат может быть несколько (например, при продлении срока),
 * актуальной считаем самую позднюю.
 */
const getMaxPlannedEliminationDate = (viol: IKnmRequirementViolation): Date | undefined => {
  const plannedDates: IKnmString[] = viol.remediationPlan?.plannedEliminationDates ?? [];
  let maxDate: Date | undefined;

  plannedDates.forEach(plannedDate => {
    const date = parseDate({ date: plannedDate?.value || '', parseFormat: DATE_FORMAT_LONG_FNS });

    if (!date) return;

    date.setHours(0, 0, 0, 0);

    if (!maxDate || date > maxDate) {
      maxDate = date;
    }
  });

  return maxDate;
};

/**
 * Нарушение подлежит повторной проверке: не устранено и срок его устранения уже наступил
 * относительно даты начала повторного мероприятия.
 */
export const checkViolationForMatchingRepeatCheck = (viol: IKnmRequirementViolation, startDate: Date | undefined) => {
  const date =
    startDate ??
    parseDate({
      date: new Date(),
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

  date?.setHours(0, 0, 0, 0);

  const maxDateFormated = getMaxPlannedEliminationDate(viol);

  return (
    (viol.status === ViolationStatusesEnum.NOT_FIXED || viol.status === ViolationStatusesEnum.APPROVING) &&
    (isAfter(date || 0, maxDateFormated || 0) || isEqual(date || 0, maxDateFormated || 0))
  );
};
